import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BulkOperationsService } from '../../core/services/bulk-operations.service';
import { finalize } from 'rxjs/internal/operators/finalize';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';
import { UserService } from 'src/sb-tools/typescript/services/user.service';
import { AssignService } from 'src/app/core/services/ticket/assign.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotificationsService } from 'angular2-notifications';
import { first } from 'rxjs/internal/operators/first';
import { BulkOperationsNotificationService } from '../../core/services/bulk-operations-notification.service';
import { Tag } from '@sb-tools/typescript/models/tag.model';
import { AddTagsModalComponent } from '@form-field-module/modal-windows/add-tags-modal/add-tags-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TagService } from '@sb-tools/typescript/services/tag.service';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TicketStatus } from '@sb-tools/typescript/enums/ticket-status.enum';
import { TicketBulkAssigneeUpdate } from '../../core/models/ticket-bulk-assignee-update.model';
import { TicketBulkAssignOperationModel } from '../../core/models/ticket-bulk-assign-operation-model.model';
import { CategorySelectorBulkComponent } from './category-selector-bulk/category-selector-bulk.component';
import { TagsComponent } from '../../ticket-details/tags/tags.component';
import { NgClass } from '@angular/common';
import { InlineSVGComponent } from '../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { AssignBlockComponent } from '../../shared/components/assign-block/assign-block.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

@Component({
    selector: 'app-ticket-bulk-operations',
    templateUrl: './ticket-bulk-operations.component.html',
    styleUrls: ['./ticket-bulk-operations.component.scss'],
    imports: [
        TranslateModule,
        CategorySelectorBulkComponent,
        TagsComponent,
        NgClass,
        InlineSVGComponent,
        AssignBlockComponent,
        TooltipModule,
        FormsModule,
        ReactiveFormsModule,
    ],
})
export class TicketBulkOperationsComponent implements OnInit, OnDestroy {
    @Input() selectedTickets;
    @Input() trackBy: number;

    @Output() ticketsClosed = new EventEmitter<number[]>();

    assignee;
    isAssigned: boolean;
    isSubmitted: boolean;
    isUpdating: boolean;
    newReplyText = new FormControl('');
    status: TicketStatus;
    tags: Tag[] = [];
    ticketStatusEnum = TicketStatus;
    usersForAssign = [];

    private unsubscriber = new Subject<void>();

    constructor(
        private bulkOperationsService: BulkOperationsService,
        private userService: UserService,
        private assignService: AssignService,
        private translateService: TranslateService,
        private notificationsService: NotificationsService,
        private bulkOperationsNotificationService: BulkOperationsNotificationService,
        private bsModalService: BsModalService,
        private tagService: TagService,
    ) {}

    ngOnInit() {
        this.userService
            .getTeammates()
            .pipe(first())
            .subscribe((data) => {
                this.usersForAssign = this.assignService.sortUsersForAssign(data);
            });
    }

    addReply(): void {
        this.isSubmitted = true;

        if (this.newReplyText.invalid) {
            return;
        }

        this.isUpdating = true;

        this.bulkOperationsService
            .addReply({
                ids: this.selectedTickets.map((t) => t.id),
                text: this.newReplyText.value,
            })
            .pipe(
                finalize(() => {
                    this.isUpdating = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((result) => {
                this.isSubmitted = false;
                this.newReplyText.setValue('');
                this.bulkOperationsNotificationService.showNotificationForTickets(result);
            });
    }

    addTags(tags: Tag[]) {
        this.isUpdating = true;
        this.bulkOperationsService
            .addTags({
                ids: this.selectedTickets.map((t) => t.id),
                tagsCollection: tags,
            })
            .pipe(
                finalize(() => {
                    this.isUpdating = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((result) => {
                this.bulkOperationsNotificationService.showNotificationForTickets(result);
            });
    }

    assign(event) {
        const isAssigned = event.data.isAssigned;
        const assignee = event.data.assignee;

        const model = new TicketBulkAssignOperationModel();
        if (isAssigned) {
            model.newAssigneeId = assignee.id;
        }

        model.ticketsToUpdate = [];

        this.selectedTickets.forEach((selectedTicket) => {
            model.ticketsToUpdate.push(new TicketBulkAssigneeUpdate(selectedTicket));
        });

        this.isUpdating = true;
        const isAssignedNewValue = isAssigned;
        this.assignService.isAssigningTicket = true;

        this.bulkOperationsService
            .assign(model)
            .pipe(
                finalize(() => {
                    this.isUpdating = false;
                    this.assignService.isAssigningTicket = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((result) => {
                this.selectedTickets.forEach((selectedTicket) => {
                    selectedTicket.isAssigned = isAssignedNewValue;
                    if (isAssignedNewValue) {
                        selectedTicket.assignee = assignee;
                    } else {
                        selectedTicket.assignee = null;
                    }

                    selectedTicket.changeDetectionMarker = new Date().getTime();
                });

                this.bulkOperationsNotificationService.showNotificationForTickets(result);
            });
    }

    linkTickets() {
        this.isUpdating = true;
        this.bulkOperationsService
            .linkTickets(this.selectedTickets.map((t) => t.id))
            .pipe(
                finalize(() => {
                    this.isUpdating = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe(() => {
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('Your tickets were linked.'),
                );
            });
    }

    openAddTagsModal() {
        const initialState = {
            entity: this,
            tags: this.tagService.globalTags,
            confirmCallback: (tags) => {
                this.addTags(tags);
            },
        };

        this.bsModalService.show(AddTagsModalComponent, {
            initialState,
        });
    }

    updateStatus(status: TicketStatus) {
        this.isUpdating = true;
        this.bulkOperationsService
            .updateStatus({
                ids: this.selectedTickets.map((t) => t.id),
                status,
            })
            .pipe(
                finalize(() => {
                    this.isUpdating = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((result) => {
                this.selectedTickets.forEach((selectedTicket) => {
                    const isOperationWasSucceededForCurrentTicket = result.succeededIds.includes(selectedTicket.id);

                    if (isOperationWasSucceededForCurrentTicket) {
                        selectedTicket.status = status;
                        if (status === TicketStatus.completed) {
                            selectedTicket.isAssigned = false;
                            selectedTicket.assignee = false;
                        }

                        selectedTicket.changeDetectionMarker = new Date().getTime();
                    }
                });

                this.bulkOperationsNotificationService.showNotificationForTickets(result);

                if (status === TicketStatus.completed && result.succeededIds.length) {
                    this.ticketsClosed.emit(result.succeededIds);
                }
            });
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
